import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  CubeIcon,
  XMarkIcon,
  ChevronDownIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { LockOpenIcon, CheckCircleIcon } from "@heroicons/react/24/solid";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import CustomTableComponent from "../../components/CustomTableComponent";
import { formatCurrencyVND } from "../../utils/helps";
import { toast } from "react-toastify";

// Animation
const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

// Dropdown trạng thái duyệt (custom, không vỡ layout)
const ApproveDropdown = ({ value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef();
  const dropdownRef = useRef();

  const statusLabel = value === "approved" ? "Đã duyệt" : "Chưa duyệt";
  const statusStyle =
    value === "approved"
      ? "bg-green-100 text-green-800"
      : "bg-yellow-100 text-yellow-800";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${statusStyle} ${
          disabled ? "opacity-60 cursor-not-allowed" : ""
        }`}
        onClick={() => !disabled && setIsOpen((v) => !v)}
      >
        <span>{statusLabel}</span>
        <ChevronDownIcon className="w-4 h-4" />
      </button>
      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          className="absolute left-0 z-[99] mt-1 w-32 rounded-lg bg-white shadow-lg border border-gray-100 py-1"
        >
          <button
            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
              value === "approved" ? "text-green-700" : ""
            }`}
            onClick={() => {
              onChange("approved");
              setIsOpen(false);
            }}
          >
            Đã duyệt
          </button>
          <button
            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
              value === "pending" ? "text-yellow-700" : ""
            }`}
            onClick={() => {
              onChange("pending");
              setIsOpen(false);
            }}
          >
            Chưa duyệt
          </button>
        </div>
      )}
    </div>
  );
};

// Modal chi tiết sản phẩm với dropdown duyệt
const ProductDetailModal = ({ slug, onClose, reloadProducts }) => {
  const [product, setProduct] = useState(null);
  const [changing, setChanging] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    ApiCustom.get(`/product-show/${slug}`)
      .then((res) => setProduct(res.data?.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleApproveChange = async (nextStatus) => {
    if (!product || product.approveStatus === nextStatus) return;
    setChanging(true);
    try {
      await ApiCustom.put(`/product-approve/${product.id}`, {
        approveStatus: nextStatus,
      });
      toast.success("Cập nhật trạng thái thành công!");
      if (typeof reloadProducts === "function") reloadProducts();
      setProduct({ ...product, approveStatus: nextStatus });
    } catch {
      toast.error("Cập nhật trạng thái duyệt thất bại!");
    } finally {
      setChanging(false);
    }
  };

  if (!slug) return null;
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
        <div className="bg-white rounded-2xl px-10 py-8 shadow-xl text-lg">
          Đang tải thông tin sản phẩm...
        </div>
      </div>
    );
  }
  if (!product) return null;

  // Trạng thái badge style
  let statusClass = "bg-gray-100 text-gray-800 border";
  let statusText = "Hiện có";
  if (product.status === "out_of_stock") {
    statusClass = "bg-yellow-50 text-yellow-700 border border-yellow-100";
    statusText = "Hết hàng";
  } else if (product.status !== "available") {
    statusClass = "bg-red-50 text-red-600 border border-red-100";
    statusText = "Ngưng cung cấp";
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
      <div
        className="relative bg-white w-full max-w-2xl mx-2 rounded-2xl p-7 shadow-xl overflow-y-auto"
        style={{ maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {/* Ảnh và tên/mã */}
        <div className="flex flex-col sm:flex-row gap-7 mb-4">
          <div className="flex-shrink-0 flex justify-center items-center">
            <div className="w-40 h-40 rounded-xl overflow-hidden border bg-gray-50 flex items-center justify-center shadow-sm">
              {product.urlImage ? (
                <img
                  src={product.urlImage}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-gray-300">No image</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 justify-center">
            <div className="text-2xl font-bold">{product.name}</div>
            <div className="text-gray-500 text-sm">
              <span className="font-medium text-gray-700">Mã sản phẩm: </span>
              <span>{product.productCode || "---"}</span>
            </div>
            <div className="text-gray-500 text-sm">
              <span className="font-medium text-gray-700">Slug: </span>
              <span>{product.slug}</span>
            </div>
            <div>
              <span
                className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${statusClass}`}
              >
                {statusText}
              </span>
            </div>
          </div>
        </div>

        {/* Mô tả */}
        {product.description && (
          <div className="mb-5 text-[15px]">
            <div className="font-semibold text-gray-700 mb-1">Mô tả</div>
            <div
              className="text-gray-700 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: product.description }}
            />
          </div>
        )}

        {/* Grid thuộc tính */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-[15px] mb-3">
          <div>
            <div>
              <span className="font-semibold text-gray-700">Đơn vị: </span>
              <span className="text-gray-800">
                {product.company?.companyName || "---"}
              </span>
            </div>

            {product.isContactPrice === true ? (
              <div>
                <span className="font-semibold text-gray-700">Giá: </span>
                <span>Liên hệ</span>
              </div>
            ) : (
              <>
                <div>
                  <span className="font-semibold text-gray-700">Giá gốc: </span>
                  <span>{formatCurrencyVND(product.price)}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">
                    Giá khuyến mãi:{" "}
                  </span>
                  <span>
                    {product.discountPrice &&
                    Number(product.discountPrice) !== 0
                      ? formatCurrencyVND(product.discountPrice)
                      : "---"}
                  </span>
                </div>
              </>
            )}
          </div>

          <div>
            <div>
              <span className="font-semibold text-gray-700">Danh mục: </span>
              <span className="text-gray-800">
                {product.category?.name || "---"}
              </span>
            </div>

            {product.isContactPrice !== true && (
              <div>
                <span className="font-semibold text-gray-700">
                  Đơn vị tính:{" "}
                </span>
                <span>{product.unit || "---"}</span>
              </div>
            )}

            <div>
              <span className="font-semibold text-gray-700">Quà tặng: </span>
              <span>{product.gift || "---"}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">
                Điều kiện quà:{" "}
              </span>
              <span>
                {product.giftCondition && Number(product.giftCondition) > 0
                  ? formatCurrencyVND(product.giftCondition)
                  : "---"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductApprovedCard = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [activeTab, setActiveTab] = useState("update"); // "update" | "sending"
  const [rowBusy, setRowBusy] = useState(null);

  // Lấy danh sách sản phẩm (giữ nguyên API, lọc ở client)
  const fetchProducts = () => {
    setLoading(true);
    ApiCustom.get("/product-show")
      .then((res) => {
        setProducts(res.data?.data?.product || []);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // NHÓM 1: yêu cầu cập nhật
  const updateList = products.filter((p) => p.requestingForUpdate === true);
  // NHÓM 2: đang gửi duyệt (không yêu cầu cập nhật)
  const sendingList = products.filter(
    (p) => !p.requestingForUpdate && p.approveStatus === "sendingForApprove",
  );

  const pendingTotal = updateList.length + sendingList.length;

  // Handlers: Mở khóa (tab update) & Duyệt (tab sending)
  const handleUnlock = async (item) => {
    try {
      setRowBusy(item.id);
      await ApiCustom.put(`/product-change-locked/${item.id}`, {
        locked: false,
      });
      toast.success("Đã mở khóa sản phẩm!");
      fetchProducts();
    } catch (e) {
      toast.error("Mở khóa thất bại!");
    } finally {
      setRowBusy(null);
    }
  };

  const handleApprove = async (item) => {
    try {
      setRowBusy(item.id);
      await ApiCustom.put(`/product-approve/${item.id}`, {
        approveStatus: "approved",
      });
      toast.success("Duyệt sản phẩm thành công!");
      fetchProducts();
    } catch (e) {
      toast.error("Duyệt sản phẩm thất bại!");
    } finally {
      setRowBusy(null);
    }
  };

  // Table header dùng chung + cột Thao tác theo tab
  const headers = useMemo(() => {
    const base = [
      {
        key: "urlImage",
        label: "Hình ảnh",
        render: (item) => (
          <div className="w-12 h-12 overflow-hidden rounded bg-gray-50 flex items-center justify-center">
            {item.urlImage ? (
              <img
                src={item.urlImage}
                alt={item.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-gray-300">No image</span>
            )}
          </div>
        ),
      },
      {
        key: "name",
        label: "Tên sản phẩm",
        render: (item) => (
          <span
            className="block font-medium text-gray-900 w-60 sm:w-70 md:w-70 truncate"
            title={item.name}
          >
            {item.name}
          </span>
        ),
      },

      {
        key: "company",
        label: "Đơn vị",
        render: (item) => (
          <span className="text-gray-700">{item.company?.companyName}</span>
        ),
      },
      {
        key: "price",
        label: "Giá tiền",
        render: (item) => (
          <span>
            {item.isContactPrice === true
              ? "Liên hệ"
              : formatCurrencyVND(item.price)}
          </span>
        ),
      },
      {
        key: "approveStatus",
        label: "Trạng thái duyệt",
        render: (item) => (
          <span
            className={
              item.approveStatus === "approved"
                ? "bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium"
                : item.approveStatus === "sendingForApprove"
                  ? "bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium"
                  : "bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-medium"
            }
          >
            {item.approveStatus === "approved"
              ? "Đã duyệt"
              : item.approveStatus === "sendingForApprove"
                ? "Đang gửi duyệt"
                : "Chưa duyệt"}
          </span>
        ),
      },
    ];

    const actionCol =
      activeTab === "update"
        ? {
            key: "action",
            label: "Thao tác",
            render: (item) => (
              <button
                type="button"
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-md text-sm text-yellow-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 shadow-sm ${
                  rowBusy === item.id ? "opacity-60 cursor-not-allowed" : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (rowBusy !== item.id) handleUnlock(item);
                }}
                disabled={rowBusy === item.id}
                title="Mở khóa sản phẩm"
              >
                <LockOpenIcon className="w-4 h-4" />
                {rowBusy === item.id ? "Đang mở..." : "Mở khóa"}
              </button>
            ),
          }
        : {
            key: "action",
            label: "Thao tác",
            render: (item) => (
              <button
                type="button"
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-md text-sm text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm ring-1 ring-emerald-200 ${
                  rowBusy === item.id ? "opacity-60 cursor-not-allowed" : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (rowBusy !== item.id) handleApprove(item);
                }}
                disabled={rowBusy === item.id}
                title="Duyệt sản phẩm"
              >
                <CheckCircleIcon className="w-4 h-4" />
                {rowBusy === item.id ? "Đang duyệt..." : "Duyệt"}
              </button>
            ),
          };

    return [...base, actionCol];
  }, [activeTab, rowBusy]);

  // Data theo tab
  const tableData = activeTab === "update" ? updateList : sendingList;

  return (
    <div className="w-full">
      {/* Card ngoài */}
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="h-full cursor-pointer transition-all p-5 rounded-2xl bg-white border border-emerald-100 shadow-md"
        onClick={() => setIsOpen(true)}
      >
        <div className="flex flex-col items-center text-center gap-2">
          <div className="p-3 bg-emerald-100 rounded-full mb-1 flex items-center justify-center">
            <CubeIcon className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <p className="text-black text-lg">Sản phẩm chờ duyệt</p>
            <p className="text-3xl font-semibold mt-1">
              {loading ? "..." : pendingTotal}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Modal danh sách (2 tab) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-7xl max-h-[90vh] mx-2 rounded-2xl p-6 shadow-xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>

              <div className="mb-4">
                <h2 className="text-xl font-semibold ">Sản phẩm chờ duyệt</h2>
              </div>

              {/* Tabs */}
              <div className="mb-4">
                <div className="inline-flex rounded-lg border bg-white p-1">
                  <button
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                      activeTab === "update"
                        ? "bg-blue-50 text-blue-700 border border-blue-200 shadow-inner"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => setActiveTab("update")}
                  >
                    Yêu cầu cập nhật
                    {!loading && (
                      <span className="ml-2 inline-block px-2 py-0.5 rounded-full border border-blue-300 bg-white text-red-500 text-sm font-semibold">
                        {updateList.length}
                      </span>
                    )}
                  </button>

                  <button
                    className={`ml-2 px-3 py-1.5 rounded-md text-sm font-medium transition ${
                      activeTab === "sending"
                        ? "bg-blue-50 text-blue-700 border border-blue-200 shadow-inner"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => setActiveTab("sending")}
                  >
                    Đang gửi duyệt
                    {!loading && (
                      <span className="ml-2 inline-block px-2 py-0.5 rounded-full border border-blue-300 bg-white text-red-500 text-sm font-semibold">
                        {sendingList.length}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Bảng theo tab */}
              {loading ? (
                <div className="text-center py-6 text-gray-400">
                  <ArrowPathIcon className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Đang tải...
                </div>
              ) : (
                <CustomTableComponent
                  headers={headers}
                  data={tableData}
                  onRowClick={(item) => setSelectedSlug(item.slug)}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal chi tiết sản phẩm */}
      <AnimatePresence>
        {selectedSlug && (
          <ProductDetailModal
            slug={selectedSlug}
            onClose={() => setSelectedSlug(null)}
            reloadProducts={fetchProducts}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductApprovedCard;
